import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CategoryBadge = ({ category }) => {
  const categoryColors = {
    'Medical': 'bg-red-100 text-red-800',
    'Grooming': 'bg-purple-100 text-purple-800',
    'Preventive': 'bg-green-100 text-green-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[category] || 'bg-gray-100 text-gray-800'}`}>
      {category}
    </span>
  );
};

export const VisitTypes = () => {
  const [visitTypes, setVisitTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Medical',
  });

  useEffect(() => {
    fetchVisitTypes();
  }, []);

  const fetchVisitTypes = async () => {
    try {
      const response = await api.get('/visit-types');
      setVisitTypes(response.data);
    } catch (error) {
      toast.error('Failed to load visit types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.post('/visit-types', { ...formData, id: editingType.id });
        toast.success('Visit type updated successfully');
      } else {
        await api.post('/visit-types', formData);
        toast.success('Visit type created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ name: '', category: 'Medical' });
      setEditingType(null);
      fetchVisitTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      category: type.category,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingTypeId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/visit-types/${deletingTypeId}`);
      toast.success('Visit type deleted successfully');
      fetchVisitTypes();
    } catch (error) {
      toast.error('Failed to delete visit type');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingTypeId(null);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="visit-types-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Visit Types</h1>
          <p className="text-gray-600">Configure available visit types for your clinic</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingType(null);
                setFormData({ name: '', category: 'Medical' });
              }}
              data-testid="add-visit-type-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Visit Type
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit Visit Type' : 'Add New Visit Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="visit-type-form">
              <div>
                <Label htmlFor="name">Visit Type Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl mt-2"
                  placeholder="e.g., Consultation, Surgery, Grooming"
                  required
                  data-testid="visit-type-name-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="visit-type-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Grooming">Grooming</SelectItem>
                    <SelectItem value="Preventive">Preventive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="visit-type-submit-button">
                {editingType ? 'Update Visit Type' : 'Create Visit Type'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visitTypes.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
            No visit types yet. Add your first visit type to get started!
          </div>
        ) : (
          visitTypes.map((type, idx) => (
            <div
              key={type.id}
              className="bg-white rounded-3xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
              data-testid={`visit-type-card-${idx}`}
            >
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-heading font-bold text-sage-800">{type.name}</h3>
                  <CategoryBadge category={type.category} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(type)}
                  className="rounded-full flex-1"
                  data-testid={`edit-visit-type-${idx}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(type.id)}
                  className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 flex-1"
                  data-testid={`delete-visit-type-${idx}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the visit type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
